/**
 * Owner self-serve "Add a property" submission + admin approval workflow
 * (20260813151100_owner_property_submission_workflow.sql).
 *
 * The wizard (/admin/properties/new) writes directly to properties/
 * apartments/rooms for staff, but an owner-portal account has no RLS grant on
 * any of those tables — every owner-facing write here goes through a
 * SECURITY DEFINER RPC instead.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { WizardState } from "@/components/admin/v2/wizard/wizardContext";
import {
  bedroomsOf,
  checklistToRows,
  rowsToChecklist,
  type ChecklistItemRow,
} from "@/components/admin/v2/wizard/checklist";
import { useLanguage } from "@/contexts/LanguageContext";
import { parseOptionalNumber, parseRequiredNumber } from "@/lib/v2-numeric";
import { normalizeVirtualTourUrl } from "@/lib/virtual-tour-url";
import { hasPlottableCoordinates } from "@/lib/coordinates";
import { composeStreetAddress } from "@/lib/address";
import { attributeOwnerChecklistPhotosToGallery } from "@/lib/checklist-gallery";

export type ApprovalStatus =
  | "pending"
  | "approved"
  | "rejected"
  /** Owner pulled their own submission; the row survives with a reason. */
  | "withdrawn";

/** Converts wizard state into the jsonb payload the submission RPCs accept. */
function wizardStateToPayload(state: WizardState) {
  return {
    title: state.property.title.trim(),
    address: state.property.address.trim(),
    // Structured street columns — printed in their own boxes on the contract
    // and the Wohnungsgeberbestätigung. The RPCs whitelist payload keys, so
    // both sides of this pairing have to move together.
    street_name: state.property.street_name.trim() || null,
    street_number: state.property.street_number.trim() || null,
    city: state.property.city.trim(),
    zip_code: state.property.zip_code.trim() || null,
    type: state.property.type || null,
    description: state.property.description.trim() || null,
    description_de: state.property.descriptionDe.trim() || null,
    images: state.property.images.filter(Boolean),
    virtual_tour_url:
      normalizeVirtualTourUrl(state.property.virtualTourUrl) || null,
    apartments: state.apartments.map((apt) => {
      const size = parseOptionalNumber(apt.size_sqm);
      const numRooms = parseOptionalNumber(apt.numRooms);
      // COL-529: the uploaded checklist photos, so the owner RPC can persist them
      // as apartment_checklist_files (the admin path does this in
      // useCreatePropertyV2; owners can't insert the rows from the browser, so the
      // SECURITY DEFINER RPC does it from this payload). Slot-keyed room/exterior
      // shots link to the "photos" item; the inventory/washer proofs to their own.
      const photoFiles = [
        ...Object.entries(apt.checklist.picPaths ?? {})
          .filter(([, path]) => !!path)
          .map(([slot, path]) => ({
            item_key: "photos",
            file_url: path,
            file_name: slot,
          })),
        ...(["ware", "washer"] as const).flatMap((which) => {
          const path = apt.checklist.equip?.[which]?.photoPath;
          return path
            ? [{ item_key: which, file_url: path, file_name: `${which}:photo` }]
            : [];
        }),
      ];
      // The checklist's photos proof is scored on the same bedroom count the
      // wizard showed the owner (bedroomsOf, empty → 0) — not num_rooms'
      // rooms.length fallback, which scored submit differently from display.
      // The RPC inserts these as apartment_checklist_items rows after the
      // apartment id exists; the WLAN password is never in here (see
      // checklistToRows — only the network name + a "provided" marker).
      return {
        label: apt.label.trim() || null,
        floor: apt.floor.trim() || null,
        size_sqm: size.ok ? size.value : null,
        num_rooms:
          numRooms.ok && numRooms.value != null
            ? numRooms.value
            : apt.rooms.length || null,
        checklist: checklistToRows(apt.checklist, {
          bedrooms: bedroomsOf(apt.numRooms),
          keynestAvailable: state.property.keynestAvailable,
        }),
        photo_files: photoFiles,
        /* No commercial terms. An owner used to propose collection_mode /
           revenue_model / fee here and staff confirmed them on approval; the
           wizard no longer shows those fields to owners, so the keys are
           omitted and the RPC leaves the columns NULL — which apartments.
           collection_mode defines as "inherit". Staff set the real terms on
           the apartment once the building is live. Deliberately omitted rather
           than sent as null: an omitted key cannot carry a stale default from
           wizard state. */
        rooms: apt.rooms
          .filter((r) => r.title.trim())
          .map((r) => {
            const price = parseRequiredNumber(r.price, 0);
            const roomSize = parseOptionalNumber(r.size_sqm);
            return {
              title: r.title.trim(),
              price: price.ok ? price.value : 0,
              size_sqm: roomSize.ok ? roomSize.value : null,
              floor: r.floor.trim() || null,
            };
          }),
      };
    }),
  };
}

export function useSubmitPropertyForApproval() {
  const qc = useQueryClient();
  const { language } = useLanguage();
  return useMutation({
    mutationFn: async (state: WizardState): Promise<number> => {
      const { data, error } = await supabase.rpc("owner_submit_property", {
        /* The UI language rides along so the decision mail can be sent in it
           when the account has no saved preference (COL-420). Inside the
           payload, not a new RPC parameter: the function signature stays put. */
        _payload: { ...wizardStateToPayload(state), language },
      });
      if (error) throw error;
      const propertyId = data as number;

      // Tell the CoLiviq team. Deliberately not awaited into the mutation's
      // failure path: the submission is saved and the owner is done, so a mail
      // provider having a bad minute must not surface as "submission failed"
      // and invite them to submit the building a second time. The Properties
      // badge still surfaces it in-app either way.
      void supabase.functions
        .invoke("notify-property-submission", { body: { propertyId } })
        .catch((err: unknown) => {
          console.error("[submit-property] admin notification failed", err);
        });

      return propertyId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
      // The admin Properties badge counts pending submissions.
      qc.invalidateQueries({ queryKey: ["v2", "pending-submission-count"] });
    },
  });
}

/**
 * How many owner submissions are waiting for review — the count behind the
 * admin Properties badge. Staff-only: `submitted_by IS NOT NULL` rows are
 * readable through the same policies the approvals table uses, and an owner
 * simply sees their own.
 */
export function usePendingSubmissionCount(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ["v2", "pending-submission-count"],
    staleTime: 60_000,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from("properties")
        .select("id", { count: "exact", head: true })
        .eq("approval_status", "pending")
        .not("submitted_by", "is", null);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

export function useResubmitProperty() {
  const qc = useQueryClient();
  const { language } = useLanguage();
  return useMutation({
    mutationFn: async (params: {
      propertyId: number;
      state: WizardState;
    }): Promise<void> => {
      const { error } = await supabase.rpc("owner_resubmit_property", {
        _property_id: params.propertyId,
        _payload: { ...wizardStateToPayload(params.state), language },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
    },
  });
}

export function useWithdrawPropertySubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      propertyId,
      reason,
    }: {
      propertyId: number;
      reason: string;
    }): Promise<void> => {
      const { error } = await supabase.rpc(
        "owner_withdraw_property_submission",
        {
          _property_id: propertyId,
          _reason: reason,
        }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
    },
  });
}

function randomLocalId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

/** Loads an existing (pending/rejected) submission back into wizard shape, for Edit. */
export function useSubmissionForEdit(propertyId: number | null) {
  return useQuery({
    queryKey: ["v2", "property-submission-edit", propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<WizardState | null> => {
      if (!propertyId) return null;
      const { data, error } = await supabase.rpc(
        "owner_get_submission_for_edit",
        {
          _property_id: propertyId,
        }
      );
      if (error) throw error;
      if (!data) return null;

      // The RPC returns jsonb: { property, apartments }.
      const payload = data as {
        property: Record<string, any>;
        apartments?: any[];
      };
      const prop = payload.property;
      const apts = payload.apartments ?? [];
      const translations = (prop.description_translations ?? {}) as Record<
        string,
        string
      >;

      // COL-529 grandfather signal: a submission that predates the checklist has
      // zero stored rows across every apartment. rowsToChecklist always rebuilds
      // a full 15-item state, so this raw "were there any rows?" check is the
      // only place the signal survives — it's lost after the map below. Owners
      // editing such a submission can't be re-gated on proofs they never had;
      // validateWizardStep reads this to skip the gate (see skipChecklistGate),
      // matching owner_resubmit_property's server-side legacy-skip.
      const checklistGrandfathered = !apts.some(
        (a) => ((a.checklist ?? []) as unknown[]).length > 0
      );

      return {
        checklistGrandfathered,
        property: {
          title: prop.title ?? "",
          // A row saved before `address` became derived-only can carry a
          // value that drifted from street_name/street_number. The wizard no
          // longer exposes a field to repair it, so recompose on hydration
          // rather than resubmit the stale value (COL-460).
          address:
            prop.street_name || prop.street_number
              ? composeStreetAddress(prop.street_name, prop.street_number)
              : (prop.address ?? ""),
          street_name: prop.street_name ?? "",
          street_number: prop.street_number ?? "",
          city: prop.city ?? "",
          zip_code: prop.zip_code ?? "",
          // Hydrate coordinates only when the stored row actually has some.
          // 0/0 is the ungeocoded sentinel, so it must come back as "unset"
          // here — otherwise re-saving a submission would re-commit the
          // placeholder that put pins in the Atlantic (COL-48 follow-up).
          lat: hasPlottableCoordinates(prop.lat, prop.lng) ? prop.lat : null,
          lng: hasPlottableCoordinates(prop.lat, prop.lng) ? prop.lng : null,
          // Re-resolved from lat/lng by useKeyNestAvailability at the wizard
          // root (COL-529); false until that lookup lands.
          keynestAvailable: false,
          type: (prop.type ?? "") as WizardState["property"]["type"],
          description: prop.description ?? translations.en ?? "",
          descriptionDe: translations.de ?? "",
          aptCount: String(apts.length || 1),
          estimatedRoomCount: "",
          images: (prop.images ?? []) as string[],
          virtualTourUrl: prop.virtual_tour_url ?? "",
          /* Staff-only, same reasoning as the apartment-level terms below:
             the owner's wizard neither shows nor submits these, so the
             read-back keeps the wizard defaults rather than the stored
             property values. */
          collectionMode: "company_collects",
          revenueModel: "flat_fee_per_room",
          coliviqFeePct: "",
          coliviqFeeAmount: "",
          masterRent: "",
        },
        apartments: apts.map((a) => ({
          localId: randomLocalId(),
          label: a.label ?? "",
          floor: a.floor ?? "",
          size_sqm: a.size_sqm != null ? String(a.size_sqm) : "",
          numRooms: a.num_rooms != null ? String(a.num_rooms) : "",
          ownerUserId: null,
          lessorId: null,
          /* Terms are staff-only and the owner's wizard neither shows nor
             submits them, so the read-back keeps the wizard default (inherit
             from the property, same as a freshly-added apartment) rather
             than the stored values: loading a term an owner cannot see, into a
             form that will not send it back, only invites a resubmit to look
             like it changed something. */
          collectionMode: null,
          revenueModel: null,
          coliviqFeePct: "",
          coliviqFeeAmount: "",
          masterRent: "",
          // Always blank here: owner_submit_property doesn't carry
          // rep_override, and the wizard hides the representor fields from
          // owner-portal submitters for that reason. An admin sets it after
          // approval, on the apartment itself.
          repName: "",
          repNote: "",
          // COL-529: rebuild checklist state from the stored rows
          // (owner_get_submission_for_edit now returns them). Without this the
          // edit read-back is empty and owner_resubmit_property re-inserts from
          // an empty payload, wiping the owner's proofs on any field edit.
          // Structured sub-fields (WLAN name, contacts, …) survive only as the
          // folded note text — see rowsToChecklist's ponytail note.
          checklist: rowsToChecklist(
            (a.checklist ?? []) as ChecklistItemRow[],
            {
              bedrooms: bedroomsOf(
                a.num_rooms != null ? String(a.num_rooms) : ""
              ),
            }
          ),
          rooms: ((a.rooms ?? []) as any[]).map((r) => ({
            localId: randomLocalId(),
            title: r.title ?? "",
            price: r.price != null ? String(r.price) : "",
            size_sqm: r.size_sqm != null ? String(r.size_sqm) : "",
            floor: r.floor ?? "",
            keynest_pickup_code: "",
            // Always blank: owner_submit_property doesn't carry a room
            // description either, and the wizard hides that field from
            // owner-portal submitters for the same reason.
            descriptionEn: "",
            descriptionDe: "",
            images: [],
          })),
        })),
        // Staff-only (COL-529 point 4); the owner edit path neither shows nor
        // sends it, so hydrate the unsent default.
        ownerEmail: {
          to: "",
          subject: "",
          body: "",
          prefilled: false,
          sent: false,
        },
      };
    },
  });
}

// ─── Admin approvals queue ──────────────────────────────────────────────────

export type PropertySubmissionRow = {
  id: number;
  title: string;
  address: string;
  city: string;
  zip_code: string | null;
  type: string | null;
  /** Shown in the review sheet — an approver should read what will publish. */
  description: string | null;
  approval_status: ApprovalStatus;
  submitted_by: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  /** Set only for approval_status 'withdrawn' — why the owner pulled it. */
  withdraw_reason: string | null;
  withdrawn_at: string | null;
  apartments_count: number;
  rooms_count: number;
  owner_name: string | null;
  owner_email: string | null;
};

/** Every owner-submitted property (any status) for the admin approvals table. */
export function useAdminPropertySubmissions() {
  return useQuery({
    queryKey: ["v2", "admin-property-submissions"],
    queryFn: async (): Promise<PropertySubmissionRow[]> => {
      const { data: props, error: pErr } = await supabase
        .from("properties")
        .select(
          "id, title, address, city, zip_code, type, description, approval_status, submitted_by, submitted_at, reviewed_at, review_note, withdraw_reason, withdrawn_at"
        )
        .not("submitted_by", "is", null)
        .order("submitted_at", { ascending: false });
      if (pErr) throw pErr;
      const rows = (props ?? []) as any[];
      if (rows.length === 0) return [];

      const propertyIds = rows.map((r) => r.id);
      const { data: apts } = await supabase
        .from("apartments")
        .select("id, property_id")
        .in("property_id", propertyIds);
      const { data: rooms } = await supabase
        .from("rooms")
        .select("id, property_id")
        .in("property_id", propertyIds);
      const aptCountByProp = new Map<number, number>();
      for (const a of (apts ?? []) as Array<{ property_id: number }>) {
        aptCountByProp.set(
          a.property_id,
          (aptCountByProp.get(a.property_id) ?? 0) + 1
        );
      }
      const roomCountByProp = new Map<number, number>();
      for (const r of (rooms ?? []) as Array<{ property_id: number }>) {
        roomCountByProp.set(
          r.property_id,
          (roomCountByProp.get(r.property_id) ?? 0) + 1
        );
      }

      const ownerIds = Array.from(
        new Set(rows.map((r) => r.submitted_by).filter(Boolean))
      );
      const { data: profiles } = ownerIds.length
        ? await supabase
            .from("profiles")
            .select("id, name, email")
            .in("id", ownerIds)
        : { data: [] as any[] };
      const profileById = new Map(
        (
          (profiles ?? []) as Array<{
            id: string;
            name: string | null;
            email: string | null;
          }>
        ).map((p) => [p.id, p])
      );

      return rows.map((r) => ({
        id: r.id,
        title: r.title,
        address: r.address,
        city: r.city,
        zip_code: r.zip_code,
        type: r.type,
        description: r.description ?? null,
        approval_status: r.approval_status,
        submitted_by: r.submitted_by,
        submitted_at: r.submitted_at,
        reviewed_at: r.reviewed_at,
        review_note: r.review_note,
        withdraw_reason: r.withdraw_reason ?? null,
        withdrawn_at: r.withdrawn_at ?? null,
        apartments_count: aptCountByProp.get(r.id) ?? 0,
        rooms_count: roomCountByProp.get(r.id) ?? 0,
        owner_name: profileById.get(r.submitted_by)?.name ?? null,
        owner_email: profileById.get(r.submitted_by)?.email ?? null,
      }));
    },
  });
}

export function useReviewPropertySubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      propertyId: number;
      decision: "approved" | "rejected";
      note?: string | null;
    }): Promise<void> => {
      const { error } = await supabase.rpc("admin_review_property_submission", {
        _property_id: params.propertyId,
        _decision: params.decision,
        _note: params.note ?? null,
      });
      if (error) throw error;

      // COL-529: an owner can't write the public property-images bucket, so the
      // checklist photos they uploaded are copied into the property/room
      // galleries now — as the approving staff member — exterior/common to the
      // property, each bedroom's shots to its room. Best-effort and only on
      // approval; a copy failure is logged, never thrown, so it cannot undo an
      // approval that already committed server-side.
      if (params.decision === "approved") {
        const copyErrors = await attributeOwnerChecklistPhotosToGallery(
          params.propertyId
        );
        if (copyErrors.length > 0) {
          console.error(
            "[review-submission] gallery photo attribution",
            copyErrors
          );
        }
      }

      /* Tell the owner. Fire-and-forget for the same reason the submission
         notification is: the decision is committed, and a mail provider having
         a bad minute must not report the approval as failed and invite a second
         click. The function reads the decision back off the row rather than
         trusting a body field, so it can only ever announce what was recorded. */
      void supabase.functions
        .invoke("notify-property-decision", {
          body: { propertyId: params.propertyId },
        })
        .catch((err: unknown) => {
          console.error("[review-submission] owner notification failed", err);
        });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "admin-property-submissions"] });
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
    },
  });
}
